import { useReactiveVar } from '@apollo/client'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import { showBestPracticeSetting } from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useReorderItems } from 'components/WorkspaceEditor/hooks/useReorderItems'
import { useSetSelectedKey } from 'components/WorkspaceEditor/NodeKeySelectionStore'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles/useTreeViewStyles'
import type { DataNode } from 'components/WorkspaceEditor/types/types'
import {
    isEditableElement,
    isManufacturerOrBODPill,
} from 'components/WorkspaceEditor/utils'
import {
    handleViewProductsWithElementItem,
    ProjectElementNodeExtended,
} from 'components/WorkspaceEditor/utils/productLibraryUtils'
import { useSelectedProjectElementNode } from 'components/WorkspaceEditor/Workspace2Store'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { BestPracticesIconWrapper } from '../../features/bestPractices/components/BestPracticesIconWrapper'
import { useBestPracticesMenu } from '../../features/bestPractices/hooks'
import { LevelOfSpecificationIcon } from '../../features/levelOfSpecification/components/LevelOfSpecificationIcon'
import {
    NodeContentRendererProps,
    NodeOrderProps,
    SpecifierNotesProps,
} from '../NodeContents'
import { withLazyLoad } from '../withLazyComponent'
import { BestPracticesSettingPopover } from './BestPracticesSettingPopover'
import { ProductLibraryButton } from './ProductLibraryButton'
import { TreeViewHoveredIcons } from './TreeViewHoveredIcons'

/**
 * Props for rendering node Title of <TreeView /> component
 */
export interface NodeTitleProps {
    /** The node to render */
    node: DataNode
    /** A flag whether the node is expanded */
    isExpanded: boolean
    /** A flag whether to show node order label */
    showNodeOrder: boolean
    /** The text value to render node order label */
    orderLabel: string
    /** Classes to style the components */
    classes: ReturnType<typeof useTreeViewStyles>
    /** a flag to determine whether to show specifier nodes or not */
    areNotesVisible?: boolean
    /** Optional tree reference for virtualization support */
    treeRef?: React.MutableRefObject<{
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }>
}

const SpecifierNotes = withLazyLoad<SpecifierNotesProps>(() =>
    import('../NodeContents').then((module) => ({
        default: module.SpecifierNotes,
    }))
)

const NodeOrder = withLazyLoad<NodeOrderProps>(() =>
    import('../NodeContents').then((module) => ({
        default: module.NodeOrder,
    }))
)

const ContentRenderer = withLazyLoad<NodeContentRendererProps>(() =>
    import('../NodeContents').then((module) => ({
        default: module.NodeContentRenderer,
    }))
)

/**
 * Renders node title with specifier notes, order label, and content
 * @param {NodeTitleProps} props - Props for rendering node title
 * @returns {ReactElement} Styled node title
 */
export const NodeTitle = memo<NodeTitleProps>(
    ({
        node,
        isExpanded,
        showNodeOrder,
        orderLabel,
        classes,
        areNotesVisible,
        treeRef,
    }) => {
        const {
            type,
            contentItemNodes,
            isHidden,
            isUserAdded,
            isProductGroup,
            isProductType,
            isProductTypeContent,
        } = node
        const {
            displayWrap,
            inactiveText,
            nodeOrderPadding,
            articleTypography,
            paragraphTypography,
            customManufacturerNode,
        } = classes
        const isContentTeamUser = isReactiveContentTeamFromStudioPage()?.value
        const isArticleNode = type === NODE_TYPES.Article
        const hasSpecialContentTypes = contentItemNodes.some(
            (node) =>
                node.type === NODE_TYPES.UnitsOfMeasure ||
                node.type === NODE_TYPES.ReplacementOption ||
                node.type === NODE_TYPES.OptionGroup
        )

        const { currentUserInfo } = useReactiveVar(userInfo)
        const { kpUserId: userId } = currentUserInfo
        const { previewFeatures } = usePreviewFeatures()

        const isContentHidden = Boolean(isHidden) && !isContentTeamUser
        const specifierNoteClasses = isContentHidden ? 'disabled' : ''
        const showArticleNote = isExpanded && isArticleNode
        const showBPSetting = useReactiveVar(showBestPracticeSetting)
        const spanRef = useRef<HTMLSpanElement>(null)
        const { onKeyDown } = useReorderItems()
        const setSelectedKey = useSetSelectedKey()
        const selectedProjectElementNode = useSelectedProjectElementNode()
        const { elementItemValue } = useElementItemShallowSelector((state) => ({
            elementItemValue: state.elementItemValue,
        }))
        const { projectId } = useParams<{
            projectId: string
        }>()

        // Best Practices menu management
        const {
            isBPMenuOpen,
            bpAnchorElement,
            openBPMenu,
            closeBPMenu,
        } = useBestPracticesMenu()

        const isProductRelatedContent =
            isContentTeamUser &&
            (isProductGroup || isProductType || isProductTypeContent)

        const isNodeEditable =
            // PBI 2407205: Allow Product Type content to be editable
            // PT keys will be blocked at the PlainText component level
            // product group contents should not be editable on SMM/OM
            !(isContentTeamUser && (isProductGroup || isProductTypeContent)) &&
            // should be added to project
            selectedProjectElementNode?.isInProject &&
            // SMM/OM content
            (isContentTeamUser ||
                // user added article
                (isArticleNode && isUserAdded) ||
                // anything other than article
                type !== NODE_TYPES.Article)

        const isProductLibraryButtonNode = String(node.key).startsWith('product-library-btn')

        const renderSpecifierNotes = (
            dataTestId = 'specifier-notes',
            isArticleNoteSpecifier = false
        ) =>
            !areNotesVisible ? (
                <></>
            ) : (
                <SpecifierNotes
                    node={node}
                    editable={isContentTeamUser}
                    isArticleNoteSpecifier={isArticleNoteSpecifier}
                    classes={specifierNoteClasses}
                    dataTestId={dataTestId}
                />
            )
        const showBestPracticesFeatureInNewWorkspace: boolean = useMemo(
            () =>
                processEnv.REACT_APP_ShowBestPracticesFeatureInNewWorkspace ===
                '1',
            [processEnv]
        )

        const shouldDisableBPMenuForUserAddedContent: boolean = useMemo(
            () =>
                processEnv.REACT_APP_EnableSmartProjectCopy !== '1' &&
                (isUserAdded ?? false),
            [processEnv, isUserAdded]
        )

        const hasParentComments = useMemo(() => {
            return (
                (node.parentComments?.length ?? 0) > 0 &&
                node.type === NODE_TYPES.Article
            )
        }, [node])

        const isManufacturerOrBODNode = useMemo(
            () => isManufacturerOrBODPill(node),
            [node]
        )
        const showBestPracticesIcons = useMemo(
            () =>
                !isManufacturerOrBODNode &&
                !isProductLibraryButtonNode &&
                showBestPracticesFeatureInNewWorkspace &&
                selectedProjectElementNode?.isInProject,
            [
                isManufacturerOrBODNode,
                isProductLibraryButtonNode,
                showBestPracticesFeatureInNewWorkspace,
                selectedProjectElementNode?.isInProject,
            ]
        )

        const handleClick = useCallback(
            (e: React.MouseEvent) => {
                setSelectedKey(node.key)

                const target = e.target as HTMLElement
                if (isEditableElement(target)) return

                spanRef.current?.focus()
            },
            [node.key, setSelectedKey]
        )

        // Best Practices right-click handler
        const handleRightClick = useCallback(
            (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                event.preventDefault()
                event.stopPropagation()

                // DataNode uses 'key' as the identifier, not 'id'
                const contentItemId = node.key

                // Only enable BP menu for articles and paragraphs (not for manufacturer/BOD nodes or product library buttons)
                const isManufacturerOrBODNode = isManufacturerOrBODPill(node)
                const isProductLibraryButtonNode =
                    processEnv.REACT_APP_EnableDynamicManufacturerListSelection ===
                        '1' && contentItemId === 'product-library-button'

                if (
                    isManufacturerOrBODNode ||
                    isProductLibraryButtonNode ||
                    !contentItemId ||
                    !projectId ||
                    !showBestPracticesFeatureInNewWorkspace ||
                    !showBPSetting ||
                    shouldDisableBPMenuForUserAddedContent
                ) {
                    return
                }

                if (event.type === 'contextmenu') {
                    openBPMenu({
                        anchorElement: event.currentTarget,
                        contentItemId: contentItemId,
                        contentItemType: node.type || '',
                        projectId: projectId,
                        // No elementId for articles/paragraphs (only for pills)
                        elementId: undefined,
                    })
                }
            },
            [
                node,
                projectId,
                openBPMenu,
                showBestPracticesFeatureInNewWorkspace,
                shouldDisableBPMenuForUserAddedContent,
                showBPSetting,
            ]
        )

        return (
            <>
                {hasParentComments &&
                    renderSpecifierNotes('specifier-notes', hasParentComments)}
                {!isArticleNode && renderSpecifierNotes('specifier-notes')}

                <span
                    ref={spanRef}
                    className={clsx(
                        displayWrap,
                        node.type !== NODE_TYPES.Article
                            ? classes.nonArticleNodes
                            : '',
                        'article-container',
                        node.isCustomManufacturer && customManufacturerNode
                    )}
                    onClick={handleClick}
                    onKeyDown={onKeyDown}
                    onContextMenu={handleRightClick}
                >
                    {previewFeatures.hasLosAutomation &&
                        !isManufacturerOrBODNode &&
                        !isProductLibraryButtonNode && (
                            <LevelOfSpecificationIcon node={node} />
                        )}
                    {showBestPracticesIcons && (
                        <BestPracticesIconWrapper
                            contentItem={node}
                            projectElementNodeId={
                                selectedProjectElementNode?.id
                            }
                        />
                    )}
                    {!isManufacturerOrBODNode &&
                        !isProductLibraryButtonNode &&
                        showBestPracticesFeatureInNewWorkspace && (
                            <BestPracticesIconWrapper
                                contentItem={node}
                                projectElementNodeId={
                                    selectedProjectElementNode?.id
                                }
                            />
                        )}
                    {!isManufacturerOrBODNode &&
                        !isProductLibraryButtonNode && (
                            <TreeViewHoveredIcons
                                node={node}
                                classes={classes}
                                treeRef={treeRef}
                            />
                        )}
                    {showNodeOrder &&
                        !isManufacturerOrBODNode &&
                        !isProductLibraryButtonNode && (
                            <NodeOrder
                                itemOrder={orderLabel}
                                classes={clsx(
                                    isContentHidden && [
                                        inactiveText,
                                        'node-order',
                                    ],
                                    hasSpecialContentTypes && nodeOrderPadding,
                                    isArticleNode
                                        ? articleTypography
                                        : paragraphTypography
                                )}
                            />
                        )}
                    {processEnv.REACT_APP_EnableDynamicManufacturerListSelection ===
                        '1' &&
                        isProductLibraryButtonNode && (
                            <ProductLibraryButton
                                tooltipText="View and Select Manufacturers and Products"
                                iconFill="#314959"
                                onClick={() =>
                                    handleViewProductsWithElementItem(
                                        selectedProjectElementNode as ProjectElementNodeExtended,
                                        elementItemValue,
                                        userId,
                                        projectId
                                    )
                                }
                                buttonClassName={classes.productLibraryButton}
                            />
                        )}
                    {!isProductLibraryButtonNode && (
                        <ContentRenderer
                            node={node}
                            inactive={isContentHidden}
                            editable={isNodeEditable}
                        />
                    )}
                </span>

                {/* Article specifier notes is rendered under node content */}
                {showArticleNote &&
                    renderSpecifierNotes('specifier-notes-smm-om')}

                {/* Best Practices Setting Popover */}
                {isBPMenuOpen && bpAnchorElement && (
                    <BestPracticesSettingPopover
                        anchorElement={bpAnchorElement}
                        isBestPractice={node.isBestPractice ?? false}
                        isGlobalBestPractice={
                            node.isGlobalBestPractice ?? false
                        }
                        isBestPracticeEditDisabled={false}
                        onCloseMainMenu={closeBPMenu}
                    />
                )}
            </>
        )
    }
)
